import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    MdBadge,
    MdCalendarMonth,
    MdCheckCircle,
    MdEdit,
    MdHistory,
    MdLock,
    MdPauseCircle,
    MdPerson,
    MdShield,
    MdVerifiedUser,
    MdVpnKey,
} from 'react-icons/md';
import { toast } from 'react-toastify';
import { useAuth } from '../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from '../../Utils/Permissions';
import { getRoleById, getAllRoles } from '../../Services/RolesService';
import { APPRoutes } from '../../Utils/Route';
import { formatUtcToLocalDateTime } from '../../Utils/DateTime';
import ViewEmptyState from '../../Components/Common/ViewEmptyState';
import BackButton from '../../Components/Common/BackButton';

const STATUS_STYLE = {
    Active: { backgroundColor: 'var(--hc-status-success-bg)', color: 'var(--hc-status-success-text-strong)', icon: MdCheckCircle },
    Inactive: { backgroundColor: 'var(--hc-status-neutral-bg)', color: 'var(--hc-table-footer-text)', icon: MdPauseCircle },
    Unknown: { backgroundColor: 'var(--hc-status-unknown-bg)', color: 'var(--hc-status-unknown-text)', icon: MdPauseCircle },
};

const PERMISSIONS_PER_PAGE = 9;

const ViewSkeleton = () => (
    <div className="container-fluid py-4 hc-page-bg min-vh-100">
        <div className="d-flex gap-2 mb-3">
            <div className="skeleton position-relative overflow-hidden rounded-pill" style={{ height: 40, width: 110 }} />
        </div>

        <div className="p-4 p-lg-5 mb-4 overflow-hidden hc-hero-banner position-relative hc-hero-banner-teal">
            <div aria-hidden="true" className="hc-hero-circle-tr position-absolute rounded-circle pe-none" />
            <div aria-hidden="true" className="hc-hero-circle-bl position-absolute rounded-circle pe-none" />
            <div className="position-relative">
                <div className="skeleton-hero mb-3 rounded-3" style={{ height: 28, width: '55%' }} />
                <div className="skeleton-hero mb-2 rounded-2" style={{ height: 18, width: '38%' }} />
                <div className="skeleton-hero position-relative overflow-hidden rounded-2" style={{ height: 18, width: '28%' }} />
            </div>
        </div>

        <div className="row g-4">
            <div className="col-lg-7">
                <div className="skeleton position-relative overflow-hidden rounded-4" style={{ height: 320 }} />
            </div>
            <div className="col-lg-5">
                <div className="skeleton position-relative overflow-hidden rounded-4" style={{ height: 320 }} />
            </div>
        </div>
    </div>
);

export default function RolesView() {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const { token, permissions, decodedToken } = useAuth();
    const userPermissionCodes = React.useMemo(
        () => resolveUserPermissionCodes({ permissions, decodedToken }),
        [permissions, decodedToken]
    );

    const [role, setRole] = React.useState(location.state?.role || null);
    const [loading, setLoading] = React.useState(!location.state?.role);
    const [permissionPage, setPermissionPage] = React.useState(1);
    const canEditRole = permissionHas(userPermissionCodes, PermissionCodes.ROLES_EDIT, decodedToken);
    const canViewList = permissionHas(userPermissionCodes, PermissionCodes.ROLES_LIST, decodedToken);

    React.useEffect(() => {
        if (!token || (location.state?.role && id !== 'first' && id !== ':id')) {
            return;
        }

        (async () => {
            try {
                setLoading(true);
                let targetId = id;
                if (!targetId || targetId === 'first' || targetId === ':id') {
                    const listRes = await getAllRoles(token);
                    const rawList = listRes?.data?.Roles || listRes?.data?.roles || listRes?.data || listRes;
                    const list = Array.isArray(rawList) ? rawList : [];
                    targetId = list[0]?.Id || list[0]?.id || null;
                }
                if (!targetId) {
                    setRole(null);
                    return;
                }
                const response = await getRoleById(targetId, token);

                if (response?.is_success) {
                    setRole(response?.data || null);
                } else if (response?.is_success === false && response?.status_code === 500) {
                    toast.error('Internal server error. Please Contact Support Team.');
                    setRole(null);
                } else {
                    toast.error(response?.message || 'Failed to load role details');
                    setRole(null);
                }
            } catch (error) {
                console.error(error);
                setRole(null);
                toast.error('An error occurred while loading role details');
            } finally {
                setLoading(false);
            }
        })();
    }, [id, token, location.state?.role]);

    const canViewRole = permissionHas(userPermissionCodes, PermissionCodes.ROLES_VIEW, decodedToken);

    if (!canViewRole) {
        return (
            <div className="container-fluid py-4">
                <div className="alert alert-danger">You do not have permission to view this role.</div>
            </div>
        );
    }

    if (loading) {
        return <ViewSkeleton />;
    }

    if (!role) {
        return (
            <ViewEmptyState
                title="No Roles Available"
                description="There are no role records available to display."
                icon={MdShield}
                canViewList={canViewList}
                backRoute={APPRoutes.ROLES}
                backLabel="Back to Roles"
            />
        );
    }

    const statusName = role?.Status || 'Unknown';
    const statusMeta = STATUS_STYLE[statusName] || STATUS_STYLE.Unknown;
    const StatusIcon = statusMeta.icon;

    const rolePermissions = Array.isArray(role?.Permissions) ? role.Permissions : [];
    const permissionCount = rolePermissions.length || (role?.PermissionIds?.length ?? 0);

    const totalPermissionPages = Math.max(1, Math.ceil(rolePermissions.length / PERMISSIONS_PER_PAGE));
    const safePage = Math.min(permissionPage, totalPermissionPages);
    const pageStart = (safePage - 1) * PERMISSIONS_PER_PAGE;
    const pagedPermissions = rolePermissions.slice(pageStart, pageStart + PERMISSIONS_PER_PAGE);

    const createdOn = formatUtcToLocalDateTime(role?.CreatedOn);
    const modifiedOn = formatUtcToLocalDateTime(role?.ModifiedOn);

    return (
        <div className="container-fluid py-4">
            <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
                {canViewList && (
                    <BackButton onClick={() => navigate(APPRoutes.ROLES)} />
                )}

                <div className="d-flex align-items-center gap-2">
                    {canEditRole ? (
                        <button
                            type="button"
                            className="btn hc-btn-next border-0 fw-semibold d-inline-flex align-items-center gap-2 px-3 py-2"
                            onClick={() => navigate(APPRoutes.ROLES_EDIT.replace(':id', role?.Id))}
                        >
                            <MdEdit size={16} />
                            <span>Edit Role</span>
                        </button>
                    ) : null}
                </div>
            </div>

            <div className="p-4 p-lg-5 mb-4 overflow-hidden hc-hero-banner position-relative hc-hero-banner-teal">
                <div aria-hidden="true" className="hc-hero-circle-tr position-absolute rounded-circle pe-none" />
                <div aria-hidden="true" className="hc-hero-circle-bl position-absolute rounded-circle pe-none" />

                <div className="row g-4 align-items-center position-relative">
                    <div className="col-lg-8">
                        <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
                            <span
                                className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill fw-semibold"
                                style={{ fontSize: '0.82rem', ...statusMeta }}
                            >
                                <StatusIcon size={14} />
                                {statusName}
                            </span>
                            {role?.IsSuperAdmin ? (
                                <span
                                    className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill fw-semibold"
                                    style={{ fontSize: '0.82rem', backgroundColor: 'var(--hc-status-pending-bg)', color: 'var(--hc-status-pending-text)' }}
                                >
                                    <MdVerifiedUser size={14} />
                                    Super Admin
                                </span>
                            ) : null}
                        </div>

                        <h2 className="fw-bold text-white mb-2" style={{ fontSize: 'clamp(1.45rem, 2.2vw, 2rem)' }}>
                            {role?.Name || '-'}
                        </h2>
                        <p className="mb-0" style={{ color: 'rgba(var(--hc-white-rgb), 0.78)', fontSize: '0.95rem' }}>
                            {role?.Description || 'No description provided for this role.'}
                        </p>
                    </div>

                    <div className="col-lg-4">
                        <div className="p-3 rounded-4" style={{ backgroundColor: 'rgba(var(--hc-white-rgb), 0.10)' }}>
                            <p className="mb-1 small" style={{ color: 'rgba(var(--hc-white-rgb), 0.65)' }}>
                                Total Permissions
                            </p>
                            <p className="fw-bold text-white mb-3" style={{ fontSize: '1.55rem', lineHeight: 1.1 }}>
                                {permissionCount}
                            </p>
                            <p className="mb-1 small" style={{ color: 'rgba(var(--hc-white-rgb), 0.65)' }}>
                                Role ID
                            </p>
                            <p className="fw-semibold text-white mb-0 text-break" style={{ fontSize: '0.85rem' }}>
                                #{role?.Id || '-'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-lg-6">
                    <div className="hc-surface hc-card-primary h-100">
                        <div className="px-4 pt-4 pb-2 d-flex align-items-center gap-2">
                            <span
                                className="d-inline-flex align-items-center justify-content-center rounded-3"
                                style={{ width: 34, height: 34, backgroundColor: 'rgba(var(--hc-accent-blue-rgb), 0.08)', color: 'var(--hc-accent-blue)' }}
                            >
                                <MdBadge size={18} className="hc-text-primary" />
                            </span>
                            <h6 className="fw-bold mb-0 hc-text-primary">Role Information</h6>
                        </div>
                        <div className="px-4 pb-4">
                            <div className="rounded-4 border p-3 mb-3 bg-light-subtle">
                                <p className="small text-muted mb-1">Role Name</p>
                                <p className="mb-0 fw-semibold text-dark">{role?.Name || '-'}</p>
                            </div>
                            <div className="rounded-4 border p-3 mb-3 bg-light-subtle">
                                <p className="small text-muted mb-1">Client</p>
                                <p className="mb-0 fw-semibold text-dark">
                                    {role?.AccountName || 'Global Role'}
                                </p>
                            </div>
                            <div className="rounded-4 border p-3 bg-light-subtle">
                                <p className="small text-muted mb-1">Description</p>
                                <p className="mb-0 fw-semibold text-dark">
                                    {role?.Description || '-'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-lg-6">
                    <div className="hc-surface hc-card-primary h-100">
                        <div className="px-4 pt-4 pb-2 d-flex align-items-center gap-2">
                            <span
                                className="d-inline-flex align-items-center justify-content-center rounded-3"
                                style={{ width: 34, height: 34, backgroundColor: 'rgba(var(--hc-accent-blue-rgb), 0.08)', color: 'var(--hc-accent-blue)' }}
                            >
                                <MdHistory size={18} />
                            </span>
                            <h6 className="fw-bold mb-0 hc-text-primary">Timeline & Ownership</h6>
                        </div>
                        <div className="px-4 pb-4">
                            <div className="row g-3 mb-3">
                                <div className="col-12 col-sm-6">
                                    <div className="rounded-4 p-3 h-100 bg-primary-subtle border border-primary-subtle">
                                        <p className="small mb-1 text-muted d-flex align-items-center gap-1">
                                            <MdCalendarMonth size={14} /> Created On
                                        </p>
                                        <p className="mb-0 fw-semibold text-dark">{createdOn}</p>
                                    </div>
                                </div>
                                <div className="col-12 col-sm-6">
                                    <div className="rounded-4 p-3 h-100 bg-primary-subtle border border-primary-subtle">
                                        <p className="small mb-1 text-muted d-flex align-items-center gap-1">
                                            <MdCalendarMonth size={14} /> Modified On
                                        </p>
                                        <p className="mb-0 fw-semibold text-dark">{modifiedOn}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="rounded-4 p-3 bg-light border">
                                <p className="small text-muted mb-3">Ownership Trail</p>
                                <div className="row g-2">
                                    <div className="col-12 col-sm-6">
                                        <div className="bg-white border rounded-3 p-3 h-100">
                                            <div className="small text-muted mb-1">Created By</div>
                                            <div className="fw-semibold text-dark d-flex align-items-center gap-2">
                                                <MdPerson size={16} className="hc-text-primary" />
                                                <span>{role?.CreatedBy || '-'}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12 col-sm-6">
                                        <div className="bg-white border rounded-3 p-3 h-100">
                                            <div className="small text-muted mb-1">Modified By</div>
                                            <div className="fw-semibold text-dark d-flex align-items-center gap-2">
                                                <MdPerson size={16} className="text-primary" />
                                                <span>{role?.ModifiedBy || '-'}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-4">
                <div className="col-12">
                    <div className="hc-surface hc-card-primary">
                        <div className="px-4 pt-4 pb-2 d-flex align-items-center justify-content-between gap-2 flex-wrap">
                            <div className="d-flex align-items-center gap-2">
                                <span
                                    className="d-inline-flex align-items-center justify-content-center rounded-3"
                                    style={{ width: 34, height: 34, backgroundColor: 'rgba(var(--hc-accent-blue-rgb), 0.08)', color: 'var(--hc-accent-blue)' }}
                                >
                                    <MdVpnKey size={18} className="hc-text-primary" />
                                </span>
                                <h6 className="fw-bold mb-0 hc-text-primary">Assigned Permissions</h6>
                            </div>
                            <span className="badge rounded-pill bg-primary-subtle text-primary fw-semibold px-3 py-2">
                                {permissionCount} total
                            </span>
                        </div>
                        <div className="px-4 pb-4">
                            {rolePermissions.length === 0 ? (
                                <div className="rounded-4 border bg-light-subtle p-4 text-center text-muted">
                                    <MdLock size={28} className="mb-2 text-secondary" />
                                    <div className="small">
                                        {role?.IsSuperAdmin
                                            ? 'Super Admin has access to all permissions implicitly.'
                                            : 'No permissions are assigned to this role.'}
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="row g-3">
                                        {pagedPermissions.map((permission) => (
                                            <div className="col-12 col-md-6 col-lg-4" key={permission.Id}>
                                                <div className="rounded-4 border p-3 h-100 bg-light-subtle">
                                                    <div className="d-flex align-items-start gap-2">
                                                        <span
                                                            className="d-inline-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                                                            style={{ width: 32, height: 32, backgroundColor: 'rgba(var(--hc-accent-blue-rgb), 0.08)', color: 'var(--hc-accent-blue)' }}
                                                        >
                                                            <MdShield size={16} />
                                                        </span>
                                                        <div className="flex-grow-1 min-w-0">
                                                            <p className="mb-1 fw-semibold text-dark" style={{ fontSize: '0.92rem' }}>
                                                                {permission.Name}
                                                            </p>
                                                            {permission.Description ? (
                                                                <p className="mb-0 small text-muted">{permission.Description}</p>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {totalPermissionPages > 1 ? (
                                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-4">
                                            <div className="small text-muted">
                                                Showing {pageStart + 1}-{Math.min(pageStart + PERMISSIONS_PER_PAGE, rolePermissions.length)} of {rolePermissions.length}
                                            </div>
                                            <div className="d-flex justify-content-end gap-2">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm hc-btn-prev px-5 rounded-3 py-2 shadow"
                                                    disabled={safePage <= 1}
                                                    onClick={() => setPermissionPage((p) => Math.max(1, p - 1))}
                                                >
                                                    Prev
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm hc-btn-next border-0 px-5 py-2 rounded-3 shadow"
                                                    disabled={safePage >= totalPermissionPages}
                                                    onClick={() => setPermissionPage((p) => Math.min(totalPermissionPages, p + 1))}
                                                >
                                                    Next
                                                </button>
                                            </div>
                                        </div>
                                    ) : null}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

